import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userAPI } from '../services/api';
import { User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../services/api';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { isAdmin, user: currentUser, token, login } = useAuth();
  const [allowRegister, setAllowRegister] = useState(true);
  const [regLoading, setRegLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    settingsAPI
      .getAllowRegister()
      .then((res) => setAllowRegister(res.allowRegister))
      .catch(() => setAllowRegister(true));
  }, []);

  const fetchUsers = async () => {
    try {
      const data: any = await userAPI.getAll();
      // 兼容后端返回非数组的情况
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (Array.isArray(data?.users)) {
        setUsers(data.users);
      } else {
        setUsers([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('确定要删除这个用户吗？')) {
      return;
    }

    try {
      await userAPI.delete(userId);
      setUsers(users.filter(user => user.id !== userId));
    } catch (err: any) {
      setError(err.response?.data?.message || '删除用户失败');
    }
  };

  const handleUpdatePassword = async (userId: string, username: string) => {
    const newPassword = prompt(`请输入用户"${username}"的新密码：`);
    if (!newPassword) {
      return;
    }

    if (newPassword.length < 6) {
      setError('密码长度不能少于6位');
      return;
    }

    try {
      await userAPI.updatePassword(userId, newPassword);
      setSuccess(`用户"${username}"的密码已成功修改`);
      setError('');
      // 3秒后清除成功消息
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || '修改密码失败');
      setSuccess('');
    }
  };

  const handleRoleChange = async (userId: string, username: string, newRole: 'admin' | 'user', prevRole: 'admin' | 'user') => {
    if (newRole === prevRole) return;
    if (newRole === 'user' && prevRole === 'admin') {
      if (!window.confirm(`确定将用户「${username}」设为普通用户吗？其将失去后台管理权限。`)) {
        return;
      }
    } else if (newRole === 'admin' && prevRole === 'user') {
      if (!window.confirm(`确定将用户「${username}」设为管理员吗？`)) {
        return;
      }
    }
    try {
      const res = await userAPI.updateRole(userId, newRole);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...res.user } : u)));
      setError('');
      setSuccess(res.message);
      setTimeout(() => setSuccess(''), 3000);
      if (currentUser?.id === userId && token) {
        login(res.user, res.token ?? token);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '更新角色失败');
      setSuccess('');
    }
  };

  const handleToggleRegister = async () => {
    setRegLoading(true);
    try {
      const res = await settingsAPI.setAllowRegister(!allowRegister);
      setAllowRegister(res.allowRegister);
    } finally {
      setRegLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center">
              <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-500 mr-4">
                ← 返回
              </Link>
              <h1 className="text-xl font-semibold text-gray-900">用户管理</h1>
            </div>
            {isAdmin && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">允许注册</span>
                <button
                  onClick={handleToggleRegister}
                  disabled={regLoading}
                  className={`w-10 h-6 flex items-center bg-gray-200 rounded-full p-1 duration-300 focus:outline-none ${allowRegister ? 'bg-green-400' : 'bg-gray-300'}`}
                  title={allowRegister ? '点击关闭注册' : '点击开启注册'}
                >
                  <div
                    className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${allowRegister ? 'translate-x-4' : ''}`}
                  />
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">用户列表</h2>
            </div>

            {error && (
              <div className="px-6 py-4 bg-red-50 border-b border-red-200">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {success && (
              <div className="px-6 py-4 bg-green-50 border-b border-green-200">
                <p className="text-green-700">{success}</p>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      用户名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      真实姓名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      角色
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      注册时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.isArray(users) &&
                    users.map((u) => (
                      <tr key={u.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {u.username}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.realName}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isAdmin ? (
                            <select
                              value={u.role}
                              onChange={(e) =>
                                void handleRoleChange(
                                  u.id,
                                  u.username,
                                  e.target.value as 'admin' | 'user',
                                  u.role
                                )
                              }
                              className="block max-w-[160px] rounded-md border border-gray-300 bg-white py-1.5 pl-2 pr-8 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                              aria-label={`${u.username} 的角色`}
                            >
                              <option value="user">普通用户</option>
                              <option value="admin">管理员</option>
                            </select>
                          ) : (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                u.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {u.role === 'admin' ? '管理员' : '普通用户'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(u.createdAt).toLocaleDateString('zh-CN')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {u.role !== 'admin' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleDeleteUser(u.id)}
                                className="text-red-600 hover:text-red-900 mr-2"
                              >
                                删除
                              </button>
                              <button
                                type="button"
                                onClick={() => handleUpdatePassword(u.id, u.username)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                修改密码
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {users.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">暂无用户</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement; 